// The accounts list's Create / Edit Account dialog (pages/account-management.tsx):
// what it checks, and what an edit sends to PATCH /api/accounts/:id.
import { z } from "zod";

/** The account as the list loaded it — the values the Edit dialog opened with. */
export interface StoredAccountFields {
  name: string;
  phone?: string | null;
  emailAddress?: string | null;
}

/** The dialog's own fields, as submitted. */
interface AccountDialogFields {
  name: string;
  phone: string;
  emailAddress?: string;
}

const digitsOf = (phone: string | null | undefined) => (phone ?? "").replace(/\D/g, "");

/**
 * The body of the Edit Account dialog's PATCH /api/accounts/:id: only the fields the
 * admin changed, and {} when they changed nothing (the dialog then sends no request).
 *
 * The dialog used to send its whole form on every save, and the route writes whatever
 * it is given:
 * - `name`, even unchanged, makes the server replace the custom URL with the name's
 *   slug. That reset a URL set by hand on the account page, and a QuickBooks import's
 *   URL too (the import slugs names by a different rule) — either way the customer's
 *   saved ordering link stopped working.
 * - `emailAddress` was never loaded into the form, so it went as "" and wiped the
 *   account's email; its reminders and receipts stopped.
 * - `phone` went digits-only, rewriting a number stored with dashes.
 * - `isActive` and `customMenu` have no control in the dialog. They went as the list
 *   last saw them, and an account with no menu of its own (NULL) got an explicit list.
 *
 * So: a name counts as changed when it differs after trimming, a phone when its digits
 * differ, an email when it differs after trimming — an emptied email goes as "", which
 * is how the account page clears one. Nothing else is the dialog's to send.
 */
export function accountUpdateBody(stored: StoredAccountFields, values: AccountDialogFields) {
  const body: { name?: string; phone?: string; emailAddress?: string } = {};

  const name = values.name.trim();
  if (name !== stored.name.trim()) body.name = name;

  const phone = digitsOf(values.phone);
  if (phone !== digitsOf(stored.phone)) body.phone = phone;

  const emailAddress = (values.emailAddress ?? "").trim();
  if (emailAddress !== (stored.emailAddress ?? "").trim()) body.emailAddress = emailAddress;

  return body;
}

const validEmail = z.string().email();

/**
 * The dialog's checks. A new account (`stored` null) has every field checked. An edit
 * checks only what the admin changed: accounts hold phones and emails this form would
 * refuse — a QuickBooks import keeps the customer's email field as typed there, which
 * can be two addresses — and one of those must not block saving another field, or push
 * the admin into "fixing" it by deleting half.
 */
export const accountFormSchema = (stored: StoredAccountFields | null) =>
  z
    .object({
      name: z.string().trim().min(1, "Name is required"),
      phone: z.string(),
      emailAddress: z.string().optional(),
      customUrl: z.string(),
      isActive: z.boolean().default(true),
      customMenu: z
        .object({
          categories: z.record(z.boolean()),
          products: z.record(z.boolean()),
        })
        .optional(),
    })
    .superRefine((values, ctx) => {
      const changed = stored ? accountUpdateBody(stored, values) : null;
      const refuse = (field: string, message: string) =>
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });

      if (!changed || changed.phone !== undefined) {
        if (!values.phone) refuse("phone", "Phone number is required");
        else if (digitsOf(values.phone).length !== 10) refuse("phone", "Phone number must be 10 digits");
      }

      const email = changed ? changed.emailAddress : values.emailAddress;
      if (email && !validEmail.safeParse(email).success) {
        refuse("emailAddress", "Please enter a valid email address");
      }

      // A new account's URL is its name's slug, so the name has to make one ("!!!" does
      // not). An edit sends no URL, and an account may have none.
      if (!stored && !values.customUrl) refuse("customUrl", "Custom URL is required");
    });

export type AccountForm = z.infer<ReturnType<typeof accountFormSchema>>;
